import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { Button, Card, Skeleton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import colors from "../../theme/colors";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import TableHeader from "../../components/Page/tableHeader";
import TableSubHeader from "../../components/Page/tableSubHeader";
import ReportReviewStatusBadge from "../../components/Page/reportReviewStatusBadge";
import { ItemLink, TableComponent } from "../../components/Table/dataGridPro";
import {
  CanReviewReport,
  FormatPeriod,
} from "../OrganizationReports/reportUtils";
import ReviewReport from "./reviewReport";
import { DownloadFile } from "../../utils/file";
import { formatDateTimeStamp } from "../../utils/date";
import API from "../../api";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;
const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 0;
`;
const DetailsContainer = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 20px;
`;
const Detail = styled.div`
  color: ${colors.grayDarkText};
  strong {
    display: block;
    color: ${colors.black};
    margin-bottom: 6px;
  }
  p {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;
const SectionNote = styled.div`
  padding: 0 20px 20px;
  color: ${colors.grayDarkText};
  font-size: 13px;
`;
const SkeletonBody = styled.div`
  padding: 20px;
`;
const ReportUnavailableCard = styled(Card)`
  padding: 20px;
`;
const ReportTable = styled(TableComponent)`
  .MuiDataGrid-toolbar, .MuiDataGrid-toolbarContainer {
    display: none;
  }
`;

const TableSkeletonBody = () => (
  <SkeletonBody>
    <Skeleton height={35} />
    <Skeleton height={35} />
    <Skeleton height={35} />
    <Skeleton height={35} />
  </SkeletonBody>
);

const OrganizationReportContainer = () => {
  const { organizationId, locationId, reportPeriod } = useParams();
  const organization = useSelector((state) => state.organization);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [report, setReport] = useState(null);
  const [reportLoaded, setReportLoaded] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const canReview = CanReviewReport({
    user,
    organizationId: report?.organizationId || organizationId,
  });
  const alreadyReviewed = report?.reviews.some(
    (review) => review.userId === user.id,
  );
  const reviewComplete = report?.reviews.length >= 2;
  const reportAvailable =
    report?.tankCount > 0 &&
    report.period < new Date().toISOString().slice(0, 7);

  const sortedTanks = useMemo(() => {
    return [...(report?.tanks || [])].sort((firstTank, secondTank) => {
      const firstHasWeight = firstTank.minimumWeight != null;
      const secondHasWeight = secondTank.minimumWeight != null;

      if (firstHasWeight !== secondHasWeight) {
        return firstHasWeight ? -1 : 1;
      }

      return firstTank.name.localeCompare(secondTank.name);
    });
  }, [report?.tanks]);

  const tankColumns = useMemo(
    () => [
      {
        headerName: "Tank Name",
        field: "name",
        display: "flex",
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ field, row }) => {
          return (
            <ItemLink to={`/${organizationId}/tanks/${row.id}`} target="_blank">
              {row[field]}
            </ItemLink>
          );
        },
      },
      { field: "locationName", headerName: "Location" },
      {
        field: "minimumWeight",
        headerName: "Minimum Weight (kg)",
        type: "number",
        valueFormatter: (value) =>
          value == null ? "" : Number(value).toFixed(2),
      },
      {
        field: "lowWeightThreshold",
        headerName: "Alarm Low Weight (kg)",
        type: "number",
      },
      {
        field: "fillEvents",
        headerName: "Fill Events",
        type: "number",
      },
      { field: "alarms", headerName: "Alarms", type: "number" },
      {
        field: "evaporationRate",
        headerName: "Median Evap Rate (kg/day)",
        type: "number",
        valueFormatter: (value) =>
          value == null ? "" : Number(value).toFixed(2),
      },
      {
        field: "evaporationThreshold",
        headerName: "Alarm High Evap Rate (kg/day)",

        type: "number",
      },
    ],
    [organizationId],
  );

  const alarmColumns = useMemo(
    () => [
      {
        field: "tankName",
        headerName: "Tank Name",
        width: 130,
        display: "flex",
        renderCell: ({ field, row }) => {
          return (
            <ItemLink
              to={`/${organizationId}/tanks/${row.tankId}`}
              target="_blank"
            >
              {row[field]}
            </ItemLink>
          );
        },
      },
      { field: "locationName", headerName: "Location", width: 190 },
      { field: "type", headerName: "Alarm Type", width: 150 },
      {
        field: "created",
        headerName: "Alarm Created",
        width: 220,
        valueFormatter: (value) => formatDateTimeStamp(value),
      },
      {
        field: "resolved",
        headerName: "Alarm Resolved",
        width: 220,
        valueFormatter: (value) =>
          value ? formatDateTimeStamp(value) : "Not resolved",
      },
      { field: "status", headerName: "Resolution Status", width: 160 },
      { field: "resolvedBy", headerName: "Resolved By", width: 190 },
      { field: "resolutionTime", headerName: "Time to Resolve", width: 150 },
    ],
    [organizationId],
  );

  const handlers = {
    saveReview: async (note) => {
      const response = await API.Reports.PostLocationMonthlyReportReview({
        organizationId,
        locationId,
        reportPeriod,
        note,
      });

      setReport(response);
      setOpenReviewModal(false);

      const id = new Date().getTime();
      dispatch(
        addNotification({
          id,
          severity: "success",
          title: "Success",
          message: "Report review saved successfully",
        }),
      );
      setTimeout(() => {
        dispatch(removeNotification(id));
      }, 3000);
    },
    download: async () => {
      setDownloading(true);
      try {
        const download = await API.Reports.GetLocationMonthlyReportPdf({
          organizationId,
          locationId,
          reportPeriod,
        });
        DownloadFile(download);
      } catch (err) {
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err.message || String(err),
          }),
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } finally {
        setDownloading(false);
      }
    },
  };

  useEffect(() => {
    let active = true;
    setReport(null);
    setReportLoaded(false);
    setReportError(null);
    setOpenReviewModal(false);

    const getReport = async () => {
      try {
        const response = await API.Reports.GetLocationMonthlyReport({
          organizationId,
          locationId,
          reportPeriod,
        });
        if (active) setReport(response);
      } catch (err) {
        console.log(err);
        if (!active) return;

        const message = err.message || String(err);
        setReportError(message);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message,
          }),
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } finally {
        if (active) setReportLoaded(true);
      }
    };

    getReport();
    return () => {
      active = false;
    };
  }, [dispatch, organizationId, locationId, reportPeriod]);

  useEffect(() => {
    dispatch(
      setBreadCrumbs([
        {
          title: organization.name || "My Organization",
          link: "/organization",
        },
        { title: "Compliance Reports", link: "/reports" },
        {
          title: report
            ? `${report.locationName} — ${FormatPeriod(report.period)}`
            : reportLoaded
              ? "Report unavailable"
              : "Loading report",
          link: `/reports/${locationId}/${reportPeriod}`,
        },
      ]),
    );
  }, [
    dispatch,
    organization.name,
    locationId,
    reportPeriod,
    report,
    reportLoaded,
  ]);

  if (reportLoaded && !report) {
    return (
      <ReportUnavailableCard>
        <h2>{reportError ? "Unable to load report" : "Report unavailable"}</h2>
        <p>
          {reportError || "There is no report for this location and period."}
        </p>
        <Button component={Link} to={`/${organizationId}/reports`}>
          Back to Compliance Reports
        </Button>
      </ReportUnavailableCard>
    );
  }

  return (
    <PageContainer aria-busy={!reportLoaded}>
      <Card>
        <TableHeader>
          <div>
            <h2>Location Monthly Compliance Report</h2>
            <TableSubHeader>
              {reportLoaded ? (
                <>
                  {report.locationName} — {FormatPeriod(report.period)}
                </>
              ) : (
                <Skeleton width={240} />
              )}
            </TableSubHeader>
          </div>
          <ActionsContainer>
            {!reportLoaded ? (
              <>
                <Skeleton width={110} height={24} />
                {canReview && <Skeleton width={150} height={40} />}
                <Skeleton width={150} height={40} />
              </>
            ) : (
              <>
                <ReportReviewStatusBadge
                  className={report.reviews.length ? "resolved" : "alarm"}
                >
                  {report.reviews.length ? "Reviewed" : "Needs Review"}
                </ReportReviewStatusBadge>
                {canReview && (
                  <Button
                    variant="contained"
                    disabled={
                      alreadyReviewed || reviewComplete || !reportAvailable
                    }
                    onClick={() => setOpenReviewModal(true)}
                  >
                    {alreadyReviewed
                      ? "Review Recorded"
                      : reviewComplete
                        ? "Two Reviews Recorded"
                        : "Review Report"}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={downloading || !reportAvailable}
                  onClick={handlers.download}
                >
                  {downloading ? "Downloading…" : "Download PDF"}
                </Button>
              </>
            )}
          </ActionsContainer>
        </TableHeader>
        <DetailsContainer>
          <Detail>
            <strong>Organization</strong>
            {organization.name ||
              (reportLoaded ? "Organization" : <Skeleton width={150} />)}
          </Detail>
          <Detail>
            <strong>Location</strong>
            {reportLoaded ? report.locationName : <Skeleton width={150} />}
          </Detail>
          <Detail>
            <strong>Reporting Period</strong>
            {FormatPeriod(reportPeriod)}
          </Detail>
          <Detail>
            <strong>Total Tanks</strong>
            {reportLoaded ? report.tankCount : <Skeleton width={50} />}
          </Detail>
          <Detail>
            <strong>Total Alarms</strong>
            {reportLoaded ? report.alarmCount : <Skeleton width={50} />}
          </Detail>
        </DetailsContainer>
      </Card>
      <Card>
        <TableHeader>
          <h2>Report Reviews</h2>
        </TableHeader>
        <DetailsContainer>
          {[0, 1].map((index) => {
            const review = report?.reviews[index];
            return (
              <Detail key={index}>
                <strong>Review {index + 1}</strong>
                {!reportLoaded ? (
                  <>
                    <Skeleton width={160} />
                    <Skeleton width={200} />
                    <Skeleton />
                  </>
                ) : review ? (
                  <>
                    <div>{review.name}</div>
                    <div>{formatDateTimeStamp(review.date)}</div>
                    <p>{review.comments || <i>No comments</i>}</p>
                  </>
                ) : (
                  "Not yet reviewed"
                )}
              </Detail>
            );
          })}
        </DetailsContainer>
        {reportLoaded && !canReview && (
          <SectionNote>
            <i>
              You have read only access. Reviewing is available to employee
              Admins, customer Supervisors and Directors, and Network Users with
              full access.
            </i>
          </SectionNote>
        )}
      </Card>
      <Card>
        <TableHeader>
          <div>
            <h2>Tank Health Report</h2>
            <TableSubHeader>
              Tank measurements and thresholds during the reporting period.
            </TableSubHeader>
          </div>
        </TableHeader>
        {reportLoaded ? (
          <ReportTable
            key={`${report.id}-tanks`}
            columns={tankColumns}
            disableColumnSorting
            disableColumnMenu
            disableColumnFilter
            rows={sortedTanks}
            getRowsFunction={async () => sortedTanks}
            initialState={{
              pagination: { paginationModel: { pageSize: -1 } },
            }}
          />
        ) : (
          <TableSkeletonBody />
        )}
      </Card>
      <Card>
        <TableHeader>
          <div>
            <h2>Location Alarms</h2>
            <TableSubHeader>
              Tank alarms during the reporting period
            </TableSubHeader>
          </div>
        </TableHeader>
        {!reportLoaded ? (
          <TableSkeletonBody />
        ) : report.alarms.length ? (
          <ReportTable
            key={`${report.id}-alarms`}
            columns={alarmColumns}
            disableColumnSorting
            disableColumnMenu
            disableColumnFilter
            getRowsFunction={async () => report.alarms}
            initialState={{
              pagination: { paginationModel: { pageSize: -1 } },
            }}
          />
        ) : (
          <SectionNote>No alarms during this reporting period</SectionNote>
        )}
      </Card>
      {openReviewModal && (
        <ReviewReport
          reportTitle={`${report.locationName} — ${FormatPeriod(report.period)}`}
          reviewerName={[user.firstName, user.lastName]
            .filter(Boolean)
            .join(" ")}
          handleClose={() => setOpenReviewModal(false)}
          handleSave={handlers.saveReview}
        />
      )}
    </PageContainer>
  );
};

export default OrganizationReportContainer;
